import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Check, X, Sparkles } from 'lucide-react';
import SEO from '@/components/SEO';
import ZazuLogo from '@/components/ZazuLogo';
import SocialFooterLinks from '@/components/SocialFooterLinks';

/**
 * Reusable /vs comparison page — audit D5.3, D5.5.
 * Feature/pricing comparison, "why choose Zazu", and FAQ (with FAQPage schema).
 *
 * Note on competitor claims: these are publicly known category positions inferred from each competitor's
 * public site. If any specific claim becomes stale, update the `competitor` object below.
 */
const VsPage = ({ competitor }) => {
  const faqs = competitor.faqs || [
    {
      q: `Is Zazu a good alternative to ${competitor.name}?`,
      a: `Zazu fits families who want faith-based curriculum and AI-adaptive learning in one place, without ads or in-app purchases. If ${competitor.name} is missing Bible content or ad-free promises are important to you, Zazu is a fit.`
    },
    {
      q: `Can I switch from ${competitor.name} to Zazu?`,
      a: `Yes. Zazu's Free plan lets you try phonics, math, and shapes at $0 — no credit card. Family and Premium plans start at $6.99/mo, and any paid plan is refundable within 30 days.`
    },
    {
      q: `Does Zazu include Bible curriculum like ${competitor.name}?`,
      a: `Bible curriculum is included in the Premium plan ($9.99/mo) or available as a standalone Christian Education subscription ($2.99/mo, 7-day free trial). Most competitors either lack faith content or bundle it into higher tiers.`
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-emerald-50/40">
      <SEO
        title={`Zazu vs ${competitor.name} — Faith-Based Alternative for Kids Learning`}
        description={`Compare Zazu and ${competitor.name} on features, pricing, and Bible curriculum. Faith-based, ad-free, COPPA-compliant learning for ages 3-10.`}
        path={competitor.path}
        schema={faqSchema}
      />

      <header className="p-4 border-b border-slate-100 bg-white/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700">
            <ArrowLeft className="w-5 h-5" />
            <ZazuLogo size="sm" />
          </Link>
          <div className="flex gap-3">
            <Link to="/pricing"><Button variant="ghost" size="sm">Pricing</Button></Link>
            <Link to="/register"><Button size="sm" className="bg-teal-700 hover:bg-teal-800">Start free</Button></Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-emerald-100 border border-emerald-200 rounded-full px-4 py-1.5 mb-5">
            <Sparkles className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-700">Comparison</span>
          </div>
          <h1 className="font-fredoka text-4xl sm:text-5xl font-bold text-slate-900 mb-4" data-testid="vs-h1">
            Zazu vs {competitor.name}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {competitor.tagline}
          </p>
        </div>

        {/* Feature comparison table */}
        <Card className="mb-10 overflow-hidden" data-testid="vs-feature-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-slate-600">Feature</th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-emerald-600">Zazu</th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-slate-500">{competitor.name}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {competitor.features.map((f, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3 text-sm text-slate-700">{f.name}</td>
                    <td className="px-4 py-3 text-center">
                      {f.zazu === true ? <Check className="w-5 h-5 text-emerald-500 mx-auto" /> :
                       f.zazu === false ? <X className="w-5 h-5 text-slate-300 mx-auto" /> :
                       <span className="text-sm font-medium text-emerald-700">{f.zazu}</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {f.competitor === true ? <Check className="w-5 h-5 text-slate-400 mx-auto" /> :
                       f.competitor === false ? <X className="w-5 h-5 text-slate-300 mx-auto" /> :
                       <span className="text-sm text-slate-600">{f.competitor}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Pricing comparison */}
        <div className="grid md:grid-cols-2 gap-4 mb-10">
          <Card className="border-2 border-emerald-200" data-testid="vs-zazu-pricing">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-emerald-700 mb-2">Zazu pricing</h3>
              <p className="text-3xl font-bold text-slate-900 mb-1">$0 <span className="text-base font-normal text-slate-500">Free forever</span></p>
              <ul className="text-sm text-slate-600 space-y-1 mt-3">
                <li>• Basic $4.99/mo · Family $6.99/mo · Premium $9.99/mo</li>
                <li>• Christian Education standalone: $2.99/mo</li>
                <li>• Volume: from $1.50/student for schools</li>
                <li>• 30-day money-back guarantee</li>
              </ul>
            </CardContent>
          </Card>
          <Card className="border-slate-200" data-testid="vs-competitor-pricing">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-slate-700 mb-2">{competitor.name} pricing</h3>
              <p className="text-3xl font-bold text-slate-900 mb-1">{competitor.pricing.headline}</p>
              <ul className="text-sm text-slate-600 space-y-1 mt-3">
                {competitor.pricing.notes.map((n, i) => (<li key={i}>• {n}</li>))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Why choose Zazu */}
        <Card className="mb-10 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Why families choose Zazu over {competitor.name}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {competitor.whyZazu.map((w, i) => (
                <div key={i} className="flex gap-3">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-700 leading-relaxed">{w}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <section className="mb-12" data-testid="vs-faq-section">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">Frequently asked questions</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <Card key={i} className="border-slate-200">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-900 mb-2">{f.q}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <div className="text-center bg-white rounded-2xl border-2 border-emerald-100 p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">See Zazu with your kids in 5 minutes</h2>
          <p className="text-slate-600 mb-6">Free forever. No credit card required.</p>
          <Link to="/register">
            <Button size="lg" className="bg-teal-700 hover:bg-teal-800 h-14 px-8 rounded-full" data-testid="vs-cta-btn">
              Start free
            </Button>
          </Link>
        </div>
      </main>

      <footer className="bg-slate-900 text-slate-400 py-10 px-4 mt-16">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex justify-center mb-4">
            <ZazuLogo size="md" className="[&_span]:text-white" />
          </div>
          <p className="text-sm mb-3">Impacting lives, one syllable at a time.</p>
          <SocialFooterLinks />
          <p className="text-xs mt-4">&copy; {new Date().getFullYear()} Zazu. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

// Competitor data — public claims verified against each competitor's public site.
// Update if their positioning changes.
export const VsABCmouse = () => (
  <VsPage competitor={{
    name: 'ABCmouse',
    path: '/vs/abcmouse',
    tagline: 'A faith-based, ad-free alternative to ABCmouse — with Bible curriculum, AI-adaptive learning, and pricing that starts at $0.',
    features: [
      { name: 'Free forever plan', zazu: true, competitor: '30-day trial only' },
      { name: 'Bible & faith curriculum', zazu: true, competitor: false },
      { name: 'AI-adaptive learning', zazu: true, competitor: false },
      { name: 'Ad-free', zazu: true, competitor: true },
      { name: 'COPPA-compliant', zazu: true, competitor: true },
      { name: 'Voice input for pre-readers', zazu: true, competitor: false },
      { name: 'Text-to-speech karaoke highlighting', zazu: true, competitor: false },
      { name: 'Ages covered', zazu: '3–10', competitor: '2–8' },
      { name: 'Standalone Christian subscription', zazu: '$2.99/mo', competitor: false },
    ],
    pricing: {
      headline: '$12.99/mo',
      notes: ['Annual: ~$79.99/yr', 'No free forever plan', 'No Bible curriculum', 'No standalone Christian option']
    },
    whyZazu: [
      'Free forever plan — try phonics, math, and shapes without a credit card.',
      'Bible curriculum built in (Premium) or standalone at $2.99/mo.',
      'AI adjusts difficulty to your child — not a fixed curriculum track.',
      '30-day money-back guarantee on any paid plan.',
    ]
  }} />
);

export const VsHomer = () => (
  <VsPage competitor={{
    name: 'Homer',
    path: '/vs/homer',
    tagline: 'Zazu offers Homer\'s reading focus plus math, science, and Bible curriculum — for families who want one app, not four.',
    features: [
      { name: 'Free forever plan', zazu: true, competitor: '30-day trial only' },
      { name: 'Reading & phonics', zazu: true, competitor: true },
      { name: 'Math', zazu: true, competitor: 'Limited' },
      { name: 'Science', zazu: true, competitor: false },
      { name: 'Bible & faith curriculum', zazu: true, competitor: false },
      { name: 'AI-adaptive learning', zazu: true, competitor: true },
      { name: 'Ad-free', zazu: true, competitor: true },
      { name: 'Ages covered', zazu: '3–10', competitor: '2–8' },
    ],
    pricing: {
      headline: '$9.99/mo',
      notes: ['Annual: ~$59.99/yr', 'Reading-first, limited math and no science', 'No Bible curriculum', 'No standalone Christian option']
    },
    whyZazu: [
      'One app for phonics, math, science, and scripture — instead of stacking multiple apps.',
      'Free forever plan for families who want to try before subscribing.',
      'Bible curriculum for homeschools that need faith-based content.',
      'Family plan covers up to 5 kids at $6.99/mo — cheaper per child than Homer.',
    ]
  }} />
);

export const VsKhanKids = () => (
  <VsPage competitor={{
    name: 'Khan Academy Kids',
    path: '/vs/khan-academy-kids',
    tagline: 'Zazu is the Christian alternative to Khan Academy Kids — with the same free-first pricing and adaptive learning, plus Bible curriculum built in.',
    features: [
      { name: 'Free plan available', zazu: true, competitor: true },
      { name: 'Bible & faith curriculum', zazu: true, competitor: false },
      { name: 'AI-adaptive learning', zazu: true, competitor: true },
      { name: 'Ad-free', zazu: true, competitor: true },
      { name: 'COPPA-compliant', zazu: true, competitor: true },
      { name: 'Parent dashboard with weekly reports', zazu: true, competitor: 'Limited' },
      { name: 'Voice input for pre-readers', zazu: true, competitor: false },
      { name: 'Text-to-speech karaoke highlighting', zazu: true, competitor: false },
      { name: 'Ages covered', zazu: '3–10', competitor: '2–8' },
      { name: 'Standalone Christian subscription', zazu: '$2.99/mo', competitor: false },
    ],
    pricing: {
      headline: 'Free (nonprofit)',
      notes: ['100% free, no paid tier', 'No Bible or faith content', 'No premium features to unlock']
    },
    whyZazu: [
      'Faith-based curriculum Khan Academy Kids doesn\'t offer — Bible stories, memory verses, worship songs.',
      'Deeper parent dashboard: weekly recap emails, PDF exports, printable reports.',
      'Voice input and karaoke read-along for pre-readers.',
      'Standalone Christian Education subscription at $2.99/mo if you only want faith content.',
    ]
  }} />
);

export default VsPage;
